/**
 * MunicipioLiveLayers — Overlay Leaflet dedicado aos municípios
 *
 * Adiciona 3 layers ao mapa (RouteMap) quando o dashboard município abre
 * uma rota do concelho:
 *   • 🔥 Hazards oficiais IPMA/EFFIS na bbox da rota (colored pins)
 *   • 🚨 SOS activos nessa rota (pin vermelho pulsante)
 *   • 👥 Users a navegar a rota agora (avatar pin com nome + heading)
 *
 * Polling every 15s. Guard duplo: só chama o endpoint se `enabled` e
 * `routeId` estiverem presentes — user normal/admin nunca chega aqui
 * porque o próprio backend recusa (403).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEV_COLOR = { yellow: '#F5C542', orange: '#F08A2B', red: '#E13B3B' };
const HAZARD_EMOJI = {
  fire: '🔥',
  fire_risk: '⛔',       // Restrição legal DL 82/2021
  wind: '🌬️',
  rain: '🌧️',
  storm: '⛈️',
  fog: '🌫️',
  snow: '❄️',
  heat: '🌡️',
  cold: '🥶',
  coastal: '🌊',
  uv: '☀️',
  other: '⚠️',
};

const emojiIcon = (emoji, bg = '#E13B3B') => L.divIcon({
  html: `<div style="
    width:52px;height:52px;border-radius:50%;
    background:${bg};border:3px solid #fff;
    display:flex;align-items:center;justify-content:center;
    font-size:24px;box-shadow:0 4px 10px rgba(0,0,0,.4);
  ">${emoji}</div>`,
  className: 'muni-live-icon',
  iconSize: [52, 52], iconAnchor: [26, 26],
});

const sosIcon = () => L.divIcon({
  html: `<div style="
    width:52px;height:52px;border-radius:50%;
    background:#E13B3B;border:3px solid #fff;
    display:flex;align-items:center;justify-content:center;
    font-size:22px;
    box-shadow:0 0 0 6px rgba(225,59,59,0.35),0 4px 12px rgba(0,0,0,.45);
    animation:pulseSos 1.4s ease-in-out infinite;
  ">🚨</div>`,
  className: 'muni-sos-icon',
  iconSize: [52, 52], iconAnchor: [26, 26],
});

const userIcon = (name, heading) => {
  const initials = (name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const rot = typeof heading === 'number' ? heading : 0;
  return L.divIcon({
    html: `
      <div style="position:relative;width:36px;height:36px;">
        <div style="
          position:absolute;top:0;left:8px;width:20px;height:8px;
          background:#0EA5E9;clip-path:polygon(50% 0%, 100% 100%, 0% 100%);
          transform:rotate(${rot}deg);transform-origin:50% 100%;
        "></div>
        <div style="
          width:34px;height:34px;border-radius:50%;
          background:#0EA5E9;border:2px solid #fff;
          display:flex;align-items:center;justify-content:center;
          color:#fff;font-weight:900;font-size:11px;letter-spacing:.5px;
          box-shadow:0 3px 8px rgba(0,0,0,.4);
        ">${initials}</div>
      </div>
    `,
    className: 'muni-user-icon',
    iconSize: [36, 36], iconAnchor: [18, 18],
  });
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  return `${Math.round(s / 3600)}h`;
};

const MunicipioLiveLayers = ({ routeId, enabled = false, refreshMs = 15000 }) => {
  const [data, setData] = useState({ hazards: [], sos: [], users_on_track: [] });

  useEffect(() => {
    if (!enabled || !routeId) return undefined;
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const res = await axios.get(`${API}/municipio/routes/${routeId}/live`, { withCredentials: true });
        if (!cancelled) setData(res.data || { hazards: [], sos: [], users_on_track: [] });
      } catch {
        // Silent — user normal/admin sem permissão recebe 403; simplesmente não vemos layers.
      }
    };
    fetchLive();
    const id = setInterval(fetchLive, refreshMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, routeId, refreshMs]);

  if (!enabled) return null;

  return (
    <>
      {/* HAZARDS */}
      {(data.hazards || []).map((h, i) => {
        // Usa centróide ou primeira coord
        const lat = h.lat ?? h.meta?.lat ?? null;
        const lon = h.lon ?? h.meta?.lon ?? null;
        if (lat == null || lon == null) return null;
        const color = SEV_COLOR[h.severity] || SEV_COLOR.red;
        const emoji = HAZARD_EMOJI[h.hazard_type] || '⚠️';
        return (
          <Marker key={`hz-${i}`} position={[lat, lon]} icon={emojiIcon(emoji, color)}>
            <Popup>
              <div className="text-sm min-w-[200px]">
                <div className="font-black mb-1">{emoji} {h.title || h.hazard_type}</div>
                {h.severity && (
                  <span style={{
                    display: 'inline-block', padding: '2px 6px', borderRadius: 3,
                    background: color, color: '#fff', fontSize: 10, fontWeight: 900, letterSpacing: .5,
                  }}>
                    {h.severity.toUpperCase()}
                  </span>
                )}
                {h.description && <div className="text-slate-600 mt-2 text-xs">{h.description}</div>}
                {h.meta?.concelho && <div className="text-slate-500 mt-1 text-xs">📍 {h.meta.concelho}</div>}
              </div>
            </Popup>
          </Marker>
        );
      })}

      {/* SOS ATIVOS */}
      {(data.sos || []).map((s) => (
        <React.Fragment key={s.sos_id}>
          <CircleMarker
            center={[s.lat, s.lon]}
            radius={30}
            pathOptions={{
              color: '#DC2626', weight: 2, opacity: 0.35, fillOpacity: 0.12, fillColor: '#DC2626',
            }}
          />
          <Marker position={[s.lat, s.lon]} icon={sosIcon()}>
            <Popup>
              <div className="text-sm min-w-[220px]">
                <div className="font-black text-red-700 mb-1">🚨 SOS EM CURSO</div>
                <div className="text-slate-800"><b>{s.user_name || 'Utilizador'}</b></div>
                {s.message && <div className="text-slate-600 italic mt-1 text-xs">&ldquo;{s.message}&rdquo;</div>}
                <div className="text-slate-500 text-xs mt-2">
                  📱 {s.battery != null ? `${Math.round(s.battery)}% bateria · ` : ''}
                  há {fmtAgo(s.created_at)}
                </div>
                {(s.municipality_responses || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <div className="text-[10px] font-bold text-emerald-700 mb-1">✅ MENSAGENS ENVIADAS</div>
                    {s.municipality_responses.slice(-2).map((r, idx) => (
                      <div key={idx} className="text-xs text-slate-700 bg-emerald-50 p-1.5 rounded mb-1">
                        {r.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        </React.Fragment>
      ))}

      {/* USERS ON-TRACK */}
      {(data.users_on_track || []).map((u) => (
        <Marker
          key={u.user_id}
          position={[u.lat, u.lon]}
          icon={userIcon(u.name, u.heading)}
        >
          <Popup>
            <div className="text-sm min-w-[180px]">
              <div className="font-bold text-slate-800">👤 {u.name}</div>
              <div className="text-xs text-slate-500 mt-1">
                {u.speed != null && <>🚗 {Math.round((u.speed || 0) * 3.6)} km/h · </>}
                atualizado há {fmtAgo(u.updated_at)}
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                {u.lat.toFixed(5)}, {u.lon.toFixed(5)}
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
};

export default MunicipioLiveLayers;
